/**
 * SurfaceAccountMenu — the avatar, and the menu that hangs off it.
 *
 * ## Why this exists rather than a third copy
 *
 * There was no shared account menu to reuse. `MinelabRttrHeader` builds its own
 * dropdown inline and `DevourTopNav` has a separate one, so a hub wiring its
 * avatar had the choice of importing a brand's header or writing a third. This
 * is the extraction: host-agnostic, no brand in it, driven entirely by props.
 *
 * ## The avatar IS the menu
 *
 * The hub bar carried a decorative avatar AND a hamburger — two controls for
 * one job, and the hamburger was the one a viewer is LESS likely to try, since
 * an account menu lives behind an avatar by convention everywhere else in the
 * product. The hamburger is gone and this is what remains.
 *
 * ## The platform group is inside it on a phone
 *
 * The sticky bar belongs to the HUB — its icon, its actions, what the member
 * has earned. On a phone there is no room for anything that is not, so the
 * DevourPlay mark and the legal links move in here, where the account and the
 * platform relationship already live. They are a second group behind a rule,
 * not more account settings: the mark heads them so the links beneath read as
 * OURS rather than as more of the surface's own options.
 */

import React from "react";

export interface SurfaceAccountMenuItem {
    id: string;
    label: string;
    href?: string;
    onClick?: () => void;
    /**
     * `sh-avm__out` sets the item apart under a rule and in the accent — what
     * both brand-challenge menus do for sign-out, because leaving is the one
     * action you must never hit by accident.
     */
    emphasis?: string;
}

export interface SurfaceAccountMenuProps {
    /**
     * Shown as the menu's first line, not as an item. Both brand-challenge
     * menus open with the member's nickname — it tells you WHOSE menu this is
     * before you read a single action, which matters most on a surface that
     * belongs to somebody else.
     */
    memberName?: string;
    /** The member's picture. The monogram is the fallback, not the default. */
    imageUrl?: string;
    /**
     * Optional — a member who has set no name has nothing to monogram. The
     * avatar still renders, because this menu is the only route to setting
     * one; hiding it would seal them out of ever having a name.
     */
    initials?: string;
    /**
     * 0-1. Drives the ring as a conic sweep — the same progress the bar states
     * in words on a wide screen, which is why the words can drop at a
     * breakpoint and this cannot: a ring survives being small, a "340 / 1,000"
     * label does not.
     */
    progress?: number;
    items: readonly SurfaceAccountMenuItem[];
    /** Shown behind a rule, under the platform mark. */
    legal?: readonly SurfaceAccountMenuItem[];
    platformLogoUrl?: string;
    /** Accessible name — "Account menu" unless a surface has better words. */
    label?: string;
}

export const SurfaceAccountMenu: React.FC<SurfaceAccountMenuProps> = ({
    imageUrl, initials, progress, memberName, items, legal = [], platformLogoUrl,
    label = "Account menu",
}) => {
    const [open, setOpen] = React.useState(false);
    const [imageFailed, setImageFailed] = React.useState(false);
    const wrapRef = React.useRef<HTMLSpanElement>(null);

    /*
     * Close on outside click and on Escape. Both, because a menu that only
     * closes one way strands whichever input the viewer is actually using —
     * Escape for a keyboard, elsewhere-tap for a phone.
     */
    React.useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const Item: React.FC<{ item: SurfaceAccountMenuItem }> = ({item}) =>
        item.href
            ? <a role="menuitem" href={item.href} className={item.emphasis}>{item.label}</a>
            : <button type="button" role="menuitem" className={item.emphasis} onClick={() => {
                item.onClick?.(); setOpen(false);
            }}>
                {item.label}
            </button>;

    return (
        <span className="sh-avw" ref={wrapRef}>
            <button
                className="sh-av"
                type="button"
                aria-label={label}
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen(o => !o)}
                /*
                 * The ring is real progress, published as a property so the
                 * conic gradient in the stylesheet can read it. Undefined leaves
                 * the token's own default rather than drawing an empty ring,
                 * which would state that the member has no progress.
                 */
                style={progress !== undefined
                    ? {"--sh-av-progress": `${Math.min(Math.max(progress, 0), 1)}turn`} as React.CSSProperties
                    : undefined}
            >
                {/*
                  * A missing upload falls back to the monogram: `cdnUrl` is the
                  * `_c` crop and that variant is absent for some older assets,
                  * which otherwise renders a broken-image glyph in the ring.
                  */}
                {imageUrl && !imageFailed
                    ? <img src={imageUrl} alt="" onError={() => setImageFailed(true)} />
                    : <i>{initials}</i>}
            </button>

            {open &&
                <span className="sh-avm" role="menu">
                    {memberName && <span className="sh-avm__u">{memberName}</span>}
                    {items.map(item => <Item key={item.id} item={item} />)}
                    {legal.length > 0 &&
                        <span className="sh-avm__legal">
                            {platformLogoUrl &&
                                <span className="sh-avm__pw">
                                    <img src={platformLogoUrl} alt="DevourPlay" />
                                </span>}
                            {/*
                              * One line with middot separators, not stacked rows.
                              * Four legal rows made the menu read as mostly legal
                              * with an account attached; inline and tiny they are
                              * the footnote they actually are.
                              */}
                            <span className="sh-avm__lgl">
                                {legal.map((item, i) =>
                                    <React.Fragment key={item.id}>
                                        {i > 0 && <i aria-hidden="true">·</i>}
                                        <Item item={item} />
                                    </React.Fragment>)}
                            </span>
                        </span>}
                </span>}
        </span>
    );
};

export default SurfaceAccountMenu;
